import React, { useState, useEffect, useMemo, useCallback } from "react";
import ClientAppShell from "../../components/ClientAppShell";
import ContentLoader from "../../components/ContentLoader";
import { Button, Pagination } from "../../components/ui";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  getProjectUserList,
  getUserActivityLogs,
  removeUserFromProject,
} from "../../services/pofile";
import {
  listProjectInvitations,
  createProjectInvitations,
  regenerateInvitation,
} from "../../services/invitationApi";
import { Plus, RefreshCw, Send, Trash2, X } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { toast } from "react-toastify";
import "../../assets/css/dashboard.css";
import "../../assets/css/sme-assessment-tracking.css";
import "../../assets/css/su-pages-theme.css";

const TABS = [
  { id: "status", label: "Team Status" },
  { id: "activity", label: "Activity Log" },
  { id: "add", label: "Add Team Members" },
  { id: "invitationLog", label: "Invitation Log" },
];

const ACTIVITY_PAGE_SIZE = 15;

const getBadgeClass = (status) => {
  switch (status) {
    case "Active":
    case "Consumed":
      return "app-status app-status--success";
    case "Inactive":
    case "Revoked":
      return "app-status app-status--danger";
    case "Pending":
      return "app-status app-status--warning";
    case "Expired":
      return "app-status app-status--muted";
    case "Invited":
      return "app-status app-status--info";
    default:
      return "app-status app-status--muted";
  }
};

const TeamManagement = () => {
  const { user } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoadingTeamMembers, setIsLoadingTeamMembers] = useState(true);
  const [serverError, setServerError] = useState("");
  const [activityLogs, setActivityLogs] = useState([]);
  const [isLoadingActivityLogs, setIsLoadingActivityLogs] = useState(false);
  const [activityError, setActivityError] = useState("");
  const [activityPage, setActivityPage] = useState(1);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [invitationsError, setInvitationsError] = useState("");
  const [isLoadingInvitations, setIsLoadingInvitations] = useState(false);
  const [invitationLogs, setInvitationLogs] = useState([]);
  const [invitationLogsError, setInvitationLogsError] = useState("");
  const [isLoadingInvitationLogs, setIsLoadingInvitationLogs] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedTab, setSelectedTab] = useState("status");
  const [inviteEmail, setInviteEmail] = useState("");
  const [emailList, setEmailList] = useState([]);
  const [showInvitePopup, setShowInvitePopup] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [inviteSuccess, setInviteSuccess] = useState("");
  const [isInviting, setIsInviting] = useState(false);
  const [resendingInvitation, setResendingInvitation] = useState(null);
  const [removingUserId, setRemovingUserId] = useState(null);

  const handleMenuSelection = (item) => {
    setSelectedItem(item);
    return item;
  };

  const refreshTeamMembers = async () => {
    if (!projectId || !userId) {
      setIsLoadingTeamMembers(false);
      return;
    }

    setIsLoadingTeamMembers(true);
    try {
      const payload = { UserId: userId, ProjectId: parseInt(projectId, 10) };
      const response = await getProjectUserList(payload);
      if (response.data) {
        setTeamMembers(response.data);
        setServerError("");
      } else {
        setTeamMembers([]);
        setServerError("No team members found.");
      }
    } catch (error) {
      setServerError("Error fetching team members. " + error.message);
    } finally {
      setIsLoadingTeamMembers(false);
    }
  };

  useEffect(() => {
    const storedProjectId = localStorage.getItem("projectId");
    if (storedProjectId) setProjectId(storedProjectId);
  }, []);

  useEffect(() => {
    if (user?.userId) {
      setUserId(user.userId);
    }
  }, [user]);

  useEffect(() => {
    refreshTeamMembers();
  }, [projectId, userId]);

  const handleRemoveMember = async (member) => {
    if (!projectId || !member?.userId) return;

    const confirmed = window.confirm(
      `Remove ${member.userName || "this user"} from the project?\n\nTheir assessment and activity data will be kept, but they will no longer have access to this project.`
    );
    if (!confirmed) return;

    try {
      setRemovingUserId(member.userId);
      setServerError("");
      await removeUserFromProject({
        userId: member.userId,
        projectId: parseInt(projectId, 10),
      });
      toast.success(`${member.userName || "User"} removed from the project.`);
      await refreshTeamMembers();
    } catch (error) {
      setServerError("Error removing team member. " + error.message);
      toast.error(error.message || "Failed to remove team member.");
    } finally {
      setRemovingUserId(null);
    }
  };

  useEffect(() => {
    const fetchLogs = async () => {
      if (selectedTab !== "activity" || !projectId) return;

      setIsLoadingActivityLogs(true);
      try {
        const payload = {
          userId: userId,
          projectId: projectId,
        };
        const result = await getUserActivityLogs(payload);
        let logsArray = [];
        if (Array.isArray(result)) {
          logsArray = result;
        } else if (Array.isArray(result.data)) {
          logsArray = result.data;
        } else {
          console.warn("Unexpected logs shape:", result);
        }

        // Newest activity first
        logsArray.sort(
          (a, b) =>
            new Date(b.logDate).getTime() - new Date(a.logDate).getTime()
        );
        setActivityLogs(logsArray);
        setActivityPage(1);
        setActivityError("");
      } catch (error) {
        setActivityLogs([]);
        setActivityPage(1);
        setActivityError("Error fetching activity logs. " + error.message);
      } finally {
        setIsLoadingActivityLogs(false);
      }
    };
    fetchLogs();
  }, [selectedTab, projectId, userId]);

  useEffect(() => {
    const fetchPendingInvitations = async () => {
      if (selectedTab !== "add" || !projectId) return;

      try {
        setIsLoadingInvitations(true);
        const invitationsArray = await listProjectInvitations(parseInt(projectId, 10));
        setPendingInvitations(invitationsArray.filter((item) => item.status === "Pending"));
        setInvitationsError("");
      } catch (error) {
        setPendingInvitations([]);
        setInvitationsError("Error fetching pending invitations: " + error.message);
      } finally {
        setIsLoadingInvitations(false);
      }
    };

    fetchPendingInvitations();
  }, [selectedTab, projectId]);

  useEffect(() => {
    const fetchInvitationLogs = async () => {
      if (selectedTab !== "invitationLog" || !projectId) return;
      try {
        setIsLoadingInvitationLogs(true);
        const logsArray = await listProjectInvitations(parseInt(projectId, 10));
        setInvitationLogs(logsArray);
        setInvitationLogsError("");
      } catch (error) {
        setInvitationLogs([]);
        setInvitationLogsError("Error fetching invitation logs: " + error.message);
      } finally {
        setIsLoadingInvitationLogs(false);
      }
    };

    fetchInvitationLogs();
  }, [selectedTab, projectId]);

  const refreshInvitationLogs = async () => {
    if (!projectId) return;

    try {
      setIsLoadingInvitationLogs(true);
      const logsArray = await listProjectInvitations(parseInt(projectId, 10));
      setInvitationLogs(logsArray);
      setInvitationLogsError("");
    } catch (error) {
      setInvitationLogsError("Error refreshing invitation logs: " + error.message);
    } finally {
      setIsLoadingInvitationLogs(false);
    }
  };

  const refreshInvitations = async () => {
    if (!projectId) return;
    try {
      setIsLoadingInvitations(true);
      const invitationsArray = await listProjectInvitations(parseInt(projectId, 10));
      setPendingInvitations(invitationsArray.filter((item) => item.status === "Pending"));
      setInvitationsError("");
    } catch (error) {
      setInvitationsError("Error refreshing invitations: " + error.message);
    } finally {
      setIsLoadingInvitations(false);
    }
  };

  const handleRegenerateInvitation = async (invitationId) => {
    if (!projectId || !invitationId || resendingInvitation) return;
    try {
      setResendingInvitation(invitationId);
      await regenerateInvitation(invitationId, { sendEmail: true });
      setInviteSuccess("Invitation regenerated and email sent when applicable.");
      setTimeout(() => setInviteSuccess(""), 3000);
      refreshInvitations();
      if (selectedTab === "invitationLog") {
        refreshInvitationLogs();
      }
    } catch (error) {
      setInviteError(`Failed to regenerate invitation: ${error.message}`);
      setTimeout(() => setInviteError(""), 3000);
    } finally {
      setResendingInvitation(null);
    }
  };

  const renderTeamMembersTable = () => {
    if (isLoadingTeamMembers) {
      return <ContentLoader compact label="Loading team members…" />;
    }
    if (teamMembers.length === 0) {
      return <p className="su-empty-hint">No team members available.</p>;
    }

    return (
      <div className="app-table-wrap app-table-wrap--refined">
        <table className="table table-striped">
          <thead>
            <tr>
              <th>S No</th>
              <th>Name</th>
              <th>Job Title</th>
              <th>Contact Info</th>
              <th>Status</th>
              <th>Assessment Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {teamMembers.map((member, i) => (
              <tr key={member.userId}>
                <td>
                  <span className="app-td__index">{i + 1}</span>
                </td>
                <td>
                  <span className="app-td__title">{member.userName}</span>
                </td>
                <td>{member.jobTitle}</td>
                <td>{member.contactInfo}</td>
                <td>
                  <span
                    className={
                      member.active
                        ? "app-status app-status--success"
                        : "app-status app-status--muted"
                    }
                  >
                    {member.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>
                  <span className="app-td__desc">{member.assessmentStatus}</span>
                </td>
                <td>
                  <div className="btn-list-actions">
                    <Button
                      variant="danger"
                      size="sm"
                      className="btn-list-delete"
                      onClick={() => handleRemoveMember(member)}
                      disabled={removingUserId === member.userId}
                      title="Remove from project"
                      aria-label={`Remove ${member.userName || "team member"}`}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const activityTotalPages = useMemo(
    () => Math.max(1, Math.ceil(activityLogs.length / ACTIVITY_PAGE_SIZE)),
    [activityLogs.length]
  );

  const paginatedActivityLogs = useMemo(() => {
    const safePage = Math.min(Math.max(activityPage, 1), activityTotalPages);
    const start = (safePage - 1) * ACTIVITY_PAGE_SIZE;
    return activityLogs.slice(start, start + ACTIVITY_PAGE_SIZE);
  }, [activityLogs, activityPage, activityTotalPages]);

  const goToActivityPage = useCallback(
    (page) => {
      const nextPage = Math.min(Math.max(page, 1), activityTotalPages);
      setActivityPage(nextPage);
    },
    [activityTotalPages]
  );

  const renderActivityLogsTable = () => {
    if (isLoadingActivityLogs) {
      return <ContentLoader compact label="Loading activity logs…" />;
    }
    if (activityError) {
      return (
        <div className="sme-tracking-alert sme-tracking-alert--error" role="alert">
          {activityError}
        </div>
      );
    }
    if (activityLogs.length === 0) {
      return <p className="su-empty-hint">No activity logs available.</p>;
    }

    const safePage = Math.min(Math.max(activityPage, 1), activityTotalPages);
    const rangeStart = (safePage - 1) * ACTIVITY_PAGE_SIZE + 1;
    const rangeEnd = Math.min(safePage * ACTIVITY_PAGE_SIZE, activityLogs.length);

    return (
      <div className="app-table-wrap app-table-wrap--refined">
        <table className="table table-striped">
          <thead>
            <tr>
              <th>S No</th>
              <th>User</th>
              <th>Event</th>
              <th>Date & Time</th>
            </tr>
          </thead>
          <tbody>
            {paginatedActivityLogs.map((log, i) => (
              <tr key={`${log.userId}-${log.logDate}-${rangeStart + i}`}>
                <td>
                  <span className="app-td__index">{rangeStart + i}</span>
                </td>
                <td>
                  <span className="app-td__title">{log.userName}</span>
                </td>
                <td>{log.eventType}</td>
                <td>{new Date(log.logDate).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {activityTotalPages > 1 && (
          <div className="su-activity-pagination">
            <p className="su-activity-pagination__meta">
              Showing {rangeStart}–{rangeEnd} of {activityLogs.length}
            </p>
            <Pagination
              page={safePage}
              totalPages={activityTotalPages}
              onPageChange={goToActivityPage}
            />
          </div>
        )}
      </div>
    );
  };

  const renderInvitationLogsTable = () => {
    if (isLoadingInvitationLogs) {
      return (
        <ContentLoader compact label="Loading invitation logs…" />
      );
    }

    if (invitationLogsError) {
      return (
        <div className="sme-tracking-alert sme-tracking-alert--error" role="alert">
          {invitationLogsError}
        </div>
      );
    }

    if (invitationLogs.length === 0) {
      return <p className="su-empty-hint">No invitation logs available.</p>;
    }

    return (
      <>
        <div className="su-table-toolbar">
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={refreshInvitationLogs}
            disabled={isLoadingInvitationLogs}
          >
            <RefreshCw size={14} aria-hidden />
            Refresh
          </Button>
        </div>
        <div className="app-table-wrap app-table-wrap--refined">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>S No</th>
                <th>Email Address</th>
                <th>Status</th>
                <th>Expires</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {invitationLogs.map((log, i) => (
                <tr key={`invlog-${log.invitationId}`}>
                  <td>
                    <span className="app-td__index">{i + 1}</span>
                  </td>
                  <td>
                    <span className="app-td__title">
                      {log.emailAddress || "Open invite"}
                    </span>
                  </td>
                  <td>
                    <span className={getBadgeClass(log.status)}>{log.status}</span>
                  </td>
                  <td>
                    {log.expiresAt
                      ? new Date(log.expiresAt).toLocaleString()
                      : "Never"}
                  </td>
                  <td>{new Date(log.createdAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const renderPendingInvitationsTable = () => {
    if (isLoadingInvitations) {
      return (
        <ContentLoader compact label="Loading invitations…" />
      );
    }

    if (invitationsError) {
      return (
        <div className="sme-tracking-alert sme-tracking-alert--error" role="alert">
          {invitationsError}
        </div>
      );
    }

    if (pendingInvitations.length === 0) {
      return <p className="su-empty-hint">No pending invitations.</p>;
    }

    return (
      <div className="app-table-wrap app-table-wrap--refined">
        <table className="table table-striped">
          <thead>
            <tr>
              <th>S No</th>
              <th>Email</th>
              <th>Expires</th>
              <th>Created</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {pendingInvitations.map((invitation, i) => (
              <tr key={`invitation-${invitation.invitationId}`}>
                <td>
                  <span className="app-td__index">{i + 1}</span>
                </td>
                <td>
                  <span className="app-td__title">
                    {invitation.emailAddress || "Open invite"}
                  </span>
                </td>
                <td>
                  {invitation.expiresAt
                    ? new Date(invitation.expiresAt).toLocaleDateString()
                    : "Never"}
                </td>
                <td>{new Date(invitation.createdAt).toLocaleDateString()}</td>
                <td>
                  <span className={getBadgeClass(invitation.status)}>
                    {invitation.status}
                  </span>
                </td>
                <td>
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={() => handleRegenerateInvitation(invitation.invitationId)}
                    disabled={Boolean(resendingInvitation)}
                    isLoading={resendingInvitation === invitation.invitationId}
                  >
                    <Send size={14} aria-hidden />
                    Regenerate
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const handleShowInvitePopup = () => {
    setInviteEmail("");
    setEmailList([]);
    setInviteError("");
    setInviteSuccess("");
    setShowInvitePopup(true);
  };

  const handleCloseInvitePopup = () => {
    if (isInviting) return;
    setShowInvitePopup(false);
  };

  const handleEmailChange = (e) => {
    setInviteEmail(e.target.value);
  };

  const addEmail = (e) => {
    e.preventDefault();
    if (isInviting) return;
    setInviteError("");
    if (!inviteEmail.trim()) {
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      setInviteError("Please enter a valid email address");
      return;
    }
    if (emailList.includes(inviteEmail)) {
      setInviteError("This email has already been added");
      return;
    }
    setEmailList([...emailList, inviteEmail]);
    setInviteEmail("");
  };

  const removeEmail = (email) => {
    if (isInviting) return;
    setEmailList(emailList.filter((e) => e !== email));
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (isInviting) return;
    setInviteError("");
    setInviteSuccess("");

    if (emailList.length === 0) {
      setInviteError("Please add at least one email address");
      return;
    }

    try {
      setIsInviting(true);
      await createProjectInvitations({
        projectId: parseInt(projectId, 10),
        emailAddresses: emailList,
        expiryPolicy: "SevenDays",
        sendEmail: true,
      });
      setInviteSuccess(
        `${emailList.length} invitation${emailList.length > 1 ? "s" : ""} sent successfully`
      );
      const payload = { UserId: userId, ProjectId: parseInt(projectId, 10) };
      const response = await getProjectUserList(payload);
      if (response.data) {
        setTeamMembers(response.data);
      }
      refreshInvitations();
      if (selectedTab === "invitationLog") {
        refreshInvitationLogs();
      }
      setTimeout(() => {
        setEmailList([]);
      }, 2000);
    } catch (error) {
      setInviteError("Failed to send invitations: " + error.message);
    } finally {
      setIsInviting(false);
    }
  };

  const handleEmailKeyPress = (e) => {
    if (isInviting) return;
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      if (inviteEmail.trim()) {
        addEmail(e);
      }
    }
  };

  const handlePaste = (e) => {
    if (isInviting) return;
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    const emails = pastedText.split(/[\s,;]+/);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const validEmails = [];
    let invalidFound = false;

    emails.forEach((email) => {
      email = email.trim();
      if (email && emailRegex.test(email) && !emailList.includes(email)) {
        validEmails.push(email);
      } else if (email) {
        invalidFound = true;
      }
    });

    if (invalidFound) {
      setInviteError("Some emails were invalid and weren't added");
    }

    if (validEmails.length > 0) {
      setEmailList([...emailList, ...validEmails]);
    }
  };

  const renderInvitePopup = () => {
    if (!showInvitePopup) return null;

    return (
      <div
        className="modal"
        style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="inviteModalTitle"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow">
            <div className="modal-header dashboard-modal-header text-white">
              <h5 className="modal-title" id="inviteModalTitle">
                Invite Team Members
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={handleCloseInvitePopup}
                disabled={isInviting}
                aria-label="Close"
              />
            </div>
            <div className="modal-body p-4">
              {inviteError && (
                <div className="sme-tracking-alert sme-tracking-alert--error mb-3" role="alert">
                  {inviteError}
                </div>
              )}

              {inviteSuccess && (
                <div className="sme-tracking-alert sme-tracking-alert--success mb-3" role="alert">
                  {inviteSuccess}
                </div>
              )}

              <form onSubmit={addEmail}>
                <div className="mb-3">
                  <label htmlFor="inviteEmail" className="form-label">
                    Email Addresses
                  </label>
                  <div className="su-invite-email-row">
                    <input
                      type="text"
                      className="form-control"
                      id="inviteEmail"
                      value={inviteEmail}
                      onChange={handleEmailChange}
                      onKeyDown={handleEmailKeyPress}
                      onPaste={handlePaste}
                      placeholder="name@company.com"
                      disabled={isInviting}
                    />
                    <button
                      className="btn btn-dashboard-cancel su-invite-add-btn"
                      type="submit"
                      disabled={!inviteEmail.trim() || isInviting}
                    >
                      Add
                    </button>
                  </div>
                  <p className="su-invite-hint">
                    Press Enter, comma, or space to add emails. You can also paste a
                    comma- or semicolon-separated list.
                  </p>
                </div>

                {emailList.length > 0 && (
                  <div className="su-invite-chips mb-4">
                    {emailList.map((email, index) => (
                      <span key={index} className="su-invite-chip">
                        {email}
                        <button
                          type="button"
                          className="su-invite-chip__remove"
                          onClick={() => removeEmail(email)}
                          disabled={isInviting}
                          aria-label={`Remove ${email}`}
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="su-invite-actions">
                  <button
                    type="button"
                    className="btn btn-dashboard-primary"
                    onClick={handleSendInvite}
                    disabled={emailList.length === 0 || isInviting}
                  >
                    {isInviting ? (
                      <>
                        <span
                          className="sme-tracking-spinner me-2"
                          style={{ width: "1rem", height: "1rem", display: "inline-block" }}
                        />
                        Sending…
                      </>
                    ) : (
                      <>
                        Send {emailList.length > 0 ? `${emailList.length} ` : ""}
                        Invitation{emailList.length !== 1 ? "s" : ""}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAddTeamMembersContent = () => (
    <div>
      <div className="su-invite-header">
        <Button variant="primary" size="md" type="button" onClick={handleShowInvitePopup}>
          <Plus size={16} aria-hidden />
          Invite Team Members
        </Button>
      </div>

      {inviteSuccess && (
        <div className="sme-tracking-alert sme-tracking-alert--success mb-3" role="alert">
          {inviteSuccess}
        </div>
      )}
      {inviteError && (
        <div className="sme-tracking-alert sme-tracking-alert--error mb-3" role="alert">
          {inviteError}
        </div>
      )}

      {renderPendingInvitationsTable()}
    </div>
  );

  const handleTabClick = (tab) => {
    setSelectedTab(tab);
  };

  return (
    <ClientAppShell
      title="Team Management"
      dataNav="team-management"
      selectedItem={selectedItem}
      onMenuSelect={handleMenuSelection}
      intro={{
        eyebrow: 'Your project team',
        description:
          'Invite colleagues, track participation, and review activity logs and invitation status from one place.',
      }}
    >
      <div className="dash-frame-wrapper">
        <div className="dash-frame-div su-team-page">
          <div
            className="sme-tracking-tabs"
            role="tablist"
            aria-label="Team management sections"
          >
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={selectedTab === tab.id}
                className={`sme-tracking-tab ${selectedTab === tab.id ? "sme-tracking-tab--active" : ""}`}
                onClick={() => handleTabClick(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="sme-tracking-tab-panels">
            {serverError && selectedTab === "status" && (
              <div className="sme-tracking-alert sme-tracking-alert--error mb-3" role="alert">
                {serverError}
              </div>
            )}

            <div
              role="tabpanel"
              className="su-tab-panel"
              hidden={selectedTab !== "status"}
            >
              {renderTeamMembersTable()}
            </div>

            <div
              role="tabpanel"
              className="su-tab-panel"
              hidden={selectedTab !== "activity"}
            >
              {renderActivityLogsTable()}
            </div>

            <div
              role="tabpanel"
              className="su-tab-panel"
              hidden={selectedTab !== "add"}
            >
              {renderAddTeamMembersContent()}
            </div>

            <div
              role="tabpanel"
              className="su-tab-panel"
              hidden={selectedTab !== "invitationLog"}
            >
              {renderInvitationLogsTable()}
            </div>
          </div>
        </div>
      </div>

      {renderInvitePopup()}
    </ClientAppShell>
  );
};

export default TeamManagement;
